/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {calendarDate} from '@ion/core/src/utils/DateUtil';
import Accordion, {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {AdditionalInfoAccordion as CurrentAdditionalInfoAccordion} from '@ion/dls/src/components/assetdetails/current/AdditionalInfoAccordion';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {resolveValue} from '@ion/dls/src/utils/ComponentUtils';
import useDeviceFields from '@ion/query/src/hooks/useDeviceFields';
import AssetAdditionalInfoContent from './AssetAdditionalInfoContent';
import {AssetDetailsSection, useRegisteredAccordion} from './assetDetailsAccordionStore';
import DeviceAdditionalInfoContent from './DeviceAdditionalInfoContent';

// mirrors AssetAdditionalInfoContent's own field computation - kept here
// rather than exported from that file since it renders a flat CardItemRow
// list while this one groups the same values into 3 current-theme sections ...
const useAssetInfoFields = (currentAsset: Device) => {
  const asset = currentAsset instanceof Device ? currentAsset : new Device(currentAsset);
  const {
    name, description, type, category, installDate,
    licence, state, year, make, model, vin, engineType,
    deviceId, deviceType, esn, deviceStatus
  } = translate('tracking:assetDetail.additionalInfo', {returnObjects: true});
  const date          = asset?.getAssetValue('installDate');
  const itemEsn       = asset?.getItemValue({deviceAttribute: 'deviceEsn', cvsAttribute: 'deviceEsn'});
  const itemName      = asset?.getItemValue({deviceAttribute: 'assetName', cvsAttribute: 'name'});
  const itemDate      = calendarDate(date);
  const fuelType      = asset.getCommonDeviceAttribute('fuelType');
  const itemStatus    = asset?.getItemValue({deviceAttribute: 'status', cvsAttribute: 'device.status'});
  const itemDeviceId  = asset?.getItemValue({deviceAttribute: 'deviceIdValue', cvsAttribute: 'device.id'});
  const fuelTypeValue = !isNil(fuelType) ? translate(`tracking:engineType.${fuelType}`) : null;
  const descriptionValue = asset.getItemValue({deviceAttribute: 'description', cvsAttribute: 'description'});
  const field = (label: string, value: unknown, extra: {isEmpty?: boolean; clampLines?: number} = {}) => ({
    label, value : resolveValue(value as any), ...extra
  });
  return {
    // left column top-to-bottom, then right column - per InfoTwoColumnGrid's
    // contract, matching the Figma "Asset Profile" left/right stacks ...
    assetProfileFields : [
      field(name, itemName, {clampLines: 3}),
      // clamped rather than marked isEmpty - per InfoTwoColumnGrid's own
      // contract, isEmpty is reserved for a field-specific placeholder
      // sentence, not resolveValue's generic "N/A" fallback every other
      // field here also uses when its underlying value is nil ...
      field(description, descriptionValue, {clampLines: 3}),
      field(installDate, itemDate),
      field(type, asset.getCommonDeviceAttribute('assetType')),
      field(category, asset.getCommonDeviceAttribute('assetCategory'))
    ],
    vehicleDetailsFields : [
      field(licence, asset?.getAssetValue('licenseNumber')),
      field(year, asset.getCommonDeviceAttribute('year')),
      field(vin, asset.getCommonDeviceAttribute('vin')),
      field(state, asset.getCommonDeviceAttribute('state')),
      field(make, asset.getCommonDeviceAttribute('make')),
      field(model, asset.getCommonDeviceAttribute('model')),
      field(engineType, fuelTypeValue)
    ],
    deviceDetailsFields : [
      field(deviceId, itemDeviceId?.toString()),
      field(esn, itemEsn),
      field(deviceType, asset?.getItemValue({deviceAttribute: 'device.type.title', cvsAttribute: 'device.type.title'})),
      field(deviceStatus, itemStatus)
    ]
  };
};

// mirrors DeviceAdditionalInfoContent's own field computation, for the
// isScDevice branch - an SC device has no Asset Profile/Vehicle Details ...
const useScDeviceInfoFields = (device: Device) => {
  const deviceId = device?.getAssetValue('id');
  const queryData = useDeviceFields({deviceId: deviceId?.toString()});
  const {name, status, serialNumber, typeName} = queryData?.device || {};
  return {
    deviceDetailsFields : [
      {label: translate('tracking:assetDetail.additionalDeviceInfo.name'), value: resolveValue(name)},
      {label: translate('tracking:assetDetail.additionalDeviceInfo.type'), value: resolveValue(typeName)},
      {label: translate('tracking:assetDetail.additionalDeviceInfo.esn'), value: resolveValue(serialNumber)},
      {label: translate('tracking:assetDetail.additionalDeviceInfo.status'), value: resolveValue(status)}
    ]
  };
};

export type AdditionalInfoAccordionProps = {
  asset: Device,
  isScDevice: boolean,
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    accordionTitle : {
      ...dlsTypography('panelCards'),
      fontFamily    : 'Roboto',
      fontWeight    : '600',
      fontSize      : '14px',
      lineHeight    : '17px',
      textTransform : 'uppercase',
      color         : dlsColor('black'),
      minHeight     : 48
    },
    accordion : {
      padding : '0 15px'
    },
    ...getAccordionWrapperStyles()
  };
});

/**
 * @AdditionalInfoAccordion
 *
 * The AdditionalInfoAccordion component is used to display any additional information
 * about an asset or device. The contents of the accordion are determined by the
 * isScDevice prop.
 *
 * ```typescript
 *
 * import AdditionalInfoAccordion from './AdditionalInfoAccordion';
 *
 * <AdditionalInfoAccordion asset={asset} isScDevice={isScDevice} />
 *
 * ```
 */
export const AdditionalInfoAccordion = ({
  asset: currentAsset, isScDevice
}: AdditionalInfoAccordionProps) => {
  const classes = useStyles();
  // both hooks are called unconditionally (Rules of Hooks) - only one
  // result is actually used, per isScDevice, same branch the legacy
  // DeviceAdditionalInfoContent/AssetAdditionalInfoContent split on ...
  const assetFields    = useAssetInfoFields(currentAsset);
  const scDeviceFields = useScDeviceInfoFields(currentAsset);
  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // current theme only, the legacy Accordion below stays uncontrolled ...
  const {
    expanded : isAdditionalInfoExpanded,
    onExpand : onAdditionalInfoExpand
  } = useRegisteredAccordion(AssetDetailsSection.additionalInfo, false, isCurrentTheme());

  return isCurrentTheme() ? (
    isScDevice ? (
      <CurrentAdditionalInfoAccordion assetProfileFields={[]}
        vehicleDetailsFields={[]}
        deviceDetailsFields={scDeviceFields.deviceDetailsFields}
        expanded={isAdditionalInfoExpanded}
        onExpand={onAdditionalInfoExpand} />
    ) : (
      <CurrentAdditionalInfoAccordion assetProfileFields={assetFields.assetProfileFields}
        vehicleDetailsFields={assetFields.vehicleDetailsFields}
        deviceDetailsFields={assetFields.deviceDetailsFields}
        expanded={isAdditionalInfoExpanded}
        onExpand={onAdditionalInfoExpand} />
    )
  ) : (
    <div className={classes.accordionWrapper}>
      <Accordion className={classes.accordion}
        header={
          <div className={classes.accordionTitle}>
            {translate('tracking:assetDetail.additionalInfo.header')}
          </div>
        }
        details={
          <>
            {isScDevice ? (
              <DeviceAdditionalInfoContent device={currentAsset} />
            ) : (
              <AssetAdditionalInfoContent asset={currentAsset} />
            )}
          </>
        }
        borderBottom={'none'}
        padding={'15px 0'}
        isCondensedVariant />
    </div>
  );
};
export default AdditionalInfoAccordion;
